import { useState } from 'react'
import { X, AlertCircle, CheckCircle } from 'lucide-react'
import { SecureInput } from './SecureInput'
import { scanInput } from '../lib/inputSecurity'

export function CreateAccountModal({ isOpen, onClose, onSwitchToSignIn }) {
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    
    // Validation
    if (!name.trim() || !email.trim() || !password.trim()) {
      setError('Please fill in all required fields')
      return
    }
    const secErr = scanInput(name) || scanInput(company)
    if (secErr) { setError(secErr); return }
    
    if (!agreedToTerms) {
      setError('Please confirm you are not a robot')
      return
    }

    setIsLoading(true)
    
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      
      const response = await fetch(`${API_URL}/api/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          password: password,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
      })

      const data = await response.json()

      if (!response.ok) {
        const errorMessage = data.errors?.email?.[0] || 
                           data.errors?.username?.[0] || 
                           data.message || 
                           'Registration failed'
        throw new Error(errorMessage)
      }

      setSuccess(true)
      setName('')
      setCompany('')
      setEmail('')
      setPassword('')
      setPhone('')
      setAgreedToTerms(false)
      
      // Show success and redirect to sign in after 2 seconds
      setTimeout(() => {
        onSwitchToSignIn()
      }, 2000)
    } catch (err) {
      console.error('[Registration] Error:', err)
      setError(err.message || 'Registration failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-overlay fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition z-10"
        >
          <X className="w-6 h-6" />
        </button>
        {/* Form Content */}
        <form onSubmit={handleSubmit} className="space-y-3 signinmain">
          {/* Title */}
          <div className="py-6 px-8 b-bt">
            <h2 className="text-2xl font-bold text-[#4D4D4D]">Create an Account</h2>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm">Account created successfully! Redirecting to login...</p>
              </div>
            </div>
          )}

          {/* Name Field */}
          <div className="py-6 px-8 signinform">
            <div className="flex items-center gap-4">
              <label className="right-space text-lg font-bold text-[#4D4D4D] w-20 flex-shrink-0">
                Name
              </label>
              <SecureInput
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                disabled={isLoading}
                className="w-full flex-1 px-3 py-1 border border-[#D6D6D6] text-[#4D4D4D] rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-[#4D4D4D] placeholder-[#D6D6D6] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>

            {/* Company Field */}
            <div className="flex items-center gap-4">
              <label className="right-space text-lg font-bold text-[#4D4D4D] w-20 flex-shrink-0">
                Company
              </label>
              <SecureInput
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Company name"
                disabled={isLoading}
                className="w-full flex-1 px-3 py-1 border border-[#D6D6D6] text-[#4D4D4D] rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-[#4D4D4D] placeholder-[#D6D6D6] disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {/* Email Field */}
            <div className="flex items-center gap-4">
              <label className="right-space text-lg font-bold text-[#4D4D4D] w-20 flex-shrink-0">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                disabled={isLoading}
                className="w-full flex-1 px-3 py-1 border border-[#D6D6D6] rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-[#4D4D4D] placeholder-[#D6D6D6] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>

            {/* Password Field */}
            <div className="flex items-center gap-4">
              <label className="right-space text-lg font-bold text-[#4D4D4D] w-20 flex-shrink-0">
                Password
              </label>
              <div className="flex-1 relative">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  disabled={isLoading}
                  className="w-full flex-1 px-3 py-1 border border-[#D6D6D6] rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-[#4D4D4D] placeholder-[#D6D6D6] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                />
              </div>
            </div>

            {/* Phone Field */}
            <div className="flex items-center gap-4">
              <label className="right-space text-lg font-bold text-[#4D4D4D] w-20 flex-shrink-0">
                Phone
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Mobile phone for 2FA"
                disabled={isLoading}
                className="w-full flex-1 px-3 py-1 border border-[#D6D6D6] text-[#4D4D4D] rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-[#4D4D4D] placeholder-[#D6D6D6] disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {/* reCAPTCHA */}
            <div className="flex items-center gap-3 pt-2 pb-2">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                disabled={isLoading}
                className="w-4 h-4 border border-gray-300 rounded accent-[#358ECD] disabled:cursor-not-allowed"
              />
              <span className="common-size16 text-sm text-[#888888]">I'm not a robot</span>
            </div>

            {/* Create Account Button */}
            <button
              type="submit"
              disabled={isLoading || success}
              className="common-size20 w-full font-bold py-2 px-2 md:py-5 md:px-4 rounded-2xl transition mt-4 bg-[#358ECD] text-white hover:bg-[#2a6fa3] transition mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating Account...' : 'CREATE ACCOUNT'}
            </button>

            {/* Sign In Link */}
            <div className="text-center pt-5">
              <p className="common-size16 text-sm text-[#4D4D4D] font-semibold">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={onSwitchToSignIn}
                  className="text-[#358ECD] hover:text-[#358ECD] underline transition"
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
